import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {WIZARD_HEADER_CLASS} from '@Libraries/social-media-wizard-library';
import {
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {useWizardStep} from '@Components/social-media/social-media.hooks';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {useWizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {isUserLoggedIn} from '@Libraries/user.library';
import styles from './social-media-wizard-content.module.scss';

interface SocialMediaWizardContentProps {
  heading: string;
  mainContent: ReactElement;
  fluidWidth?: boolean;
  previewContent?: ReactElement;
  size?: SocialMediaWizardContentSize;
}

export function SocialMediaWizardContent({
  heading,
  mainContent,
  fluidWidth = false,
  previewContent = undefined,
  size = SocialMediaWizardContentSize.XLARGE,
}: SocialMediaWizardContentProps): ReactElement {
  const isWeb = size === SocialMediaWizardContentSize.XLARGE || size === SocialMediaWizardContentSize.LARGE || size === SocialMediaWizardContentSize.MEDIUM;
  const wizardStep = useWizardStep();
  const wizardMobileNavView = useWizardMobileNavView();
  const {isSmallScreen, isExtraSmallScreen, isMediumScreen, isExtraLargeScreen, isLargeScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const isLoggedIn = isUserLoggedIn();
  const getContentContainerStyles = (): string => {
    if (size === SocialMediaWizardContentSize.XSMALL) {
      return `${styles.xsmall} ${isAddDetailsPreview() ? styles.overflowHidden : ''}`;
    }

    if (size === SocialMediaWizardContentSize.SMALL) {
      return `${styles.small} ${isAddDetailsPreview() ? styles.overflowHidden : ''}`;
    }

    if (size === SocialMediaWizardContentSize.MEDIUM) {
      return `${styles.medium}`;
    }

    if (size === SocialMediaWizardContentSize.LARGE) {
      return `${styles.large}`;
    }

    return `${styles.xlarge}`;
  };

  const isAddDetailsPreview = (): boolean => {
    return wizardStep === SocialMediaWizardStep.ADD_DETAILS && wizardMobileNavView === WizardMobileNavView.PREVIEW;
  };

  const getWizardHeaderClasses = (): string => {
    let topSpacingClass = '';
    if (size === SocialMediaWizardContentSize.XSMALL) {
      topSpacingClass = 'spacing-m-t-4';
    }

    return `_full-width ct ${topSpacingClass} ${wizardStep !== SocialMediaWizardStep.SELECT_DESIGN ? 'spacing-m-b-5' : 'spacing-m-b-3'} ${BLURABLE_CLASS_IN_AI_CAPTION_MODE}`;
  };

  const getWizardHeading = (): ReactElement | null => {
    return (
      <div className={getWizardHeaderClasses()}>
        <span className={`body-xl-bold flex-vertical-center ${WIZARD_HEADER_CLASS} ${styles.wizardContentHeading}`}>{heading}</span>
      </div>
    );
  };

  const getWizardContentWeb = (): ReactElement => {
    return (
      <>
        {getWizardHeading()}
        <div className="flex-h-row _full-width">
          <div className={`${!previewContent ? styles.wide : 'spacing-m-r-10'} ${styles.content}`}>{mainContent}</div>
          <div className={`${!previewContent ? '_hidden' : ''} ${styles.content}`}>{previewContent}</div>
        </div>
      </>
    );
  };

  const getHeadingMargins = (): string => {
    if (wizardMobileNavView === WizardMobileNavView.PREVIEW) {
      return '';
    }

    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      return `spacing-m-b-8`;
    }

    return `spacing-m-b-4`;
  };

  const getHeadingForMobile = (): ReactElement | null => {
    return wizardMobileNavView === WizardMobileNavView.CONTENT ? (
      <span className={`${isExtraSmallScreen ? `body-m-bold` : `body-xl-bold`} flex-vertical-center ${WIZARD_HEADER_CLASS} ${styles.wizardContentHeading}`}>{heading}</span>
    ) : null;
  };

  const getWizardContentMobile = (): ReactElement => {
    return (
      <div className={`_full-width spacing-m-b-5 ${styles.mobileWrapper} spacing-p-r-4 spacing-p-l-4`}>
        <div className={`flex-h-row flex-content-center ${getHeadingMargins()}`}>{getHeadingForMobile()}</div>
        {wizardMobileNavView === WizardMobileNavView.CONTENT || !previewContent ? mainContent : previewContent}
      </div>
    );
  };

  const getMarginForContentWrapper = (): string => {
    if (!isSmallScreen && !isExtraSmallScreen) {
      return `spacing-m-t-10`;
    }

    if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
      if (size === SocialMediaWizardContentSize.XSMALL && wizardStep === SocialMediaWizardStep.SELECT_DESIGN) {
        return '';
      }

      let classes = ``;
      if (isExtraSmallScreen || isSmallScreen) {
        classes = `${styles.mobileContentPadding}`;
      }

      return `${classes} spacing-m-t-6`;
    }

    if (wizardMobileNavView === WizardMobileNavView.PREVIEW) {
      return `spacing-m-t-4`;
    }

    return `spacing-m-t-10`;
  };

  const getContentClasses = (): string => {
    return `
    flex-v-row _full-width flex-center
    ${styles.contentContainer}  
    ${getMarginForContentWrapper()} 
    ${fluidWidth ? styles.fluid : ''} 
    ${isLoggedIn ? '' : '_unpadded-bottom'}
    ${getContentContainerStyles()}
   `;
  };

  return <div className={getContentClasses()}>{isWeb || wizardStep === SocialMediaWizardStep.SELECT_DESIGN ? getWizardContentWeb() : getWizardContentMobile()}</div>;
}
